import { Routes, RouterModule, PreloadAllModules } from '@angular/router';
import { NgModule } from '@angular/core';
import { AuthorizationNeededGuard } from '@shared/guards/authorization-needed.guard';
import { HasPinGuard } from '@shared/guards/has-pin.guard';
import { ProfileRootGuard } from '@shared/guards/profile-root-guard';
import { MainCmsGuard } from '@shared/guards/root-guard';
import { isSuspendedGuard } from '@shared/guards/isSuspended.guard';
import { SchedulerEnabledGuard } from '@shared/guards/scheduler-enabled.guard';
import { MessagingEnabledGuard } from '@shared/guards/messaging-enabled.guard';
import { GroupChatEnabledGuard } from '@shared/guards/group-chat-enabled.guard';
import { CoachingEnabledGuard } from '@shared/guards/coaching-enabled.guard';
import { TeamsEnabledGuard } from '@shared/guards/teams-enabled.guard';
import { GiftCardsEnabledGuard } from '@shared/guards/gift-cards-enabled.guard';
import { AppTryoutEnabledGuard } from '@shared/guards/app-tryout-enabled.guard';
import { ReferralCodesEnabledGuard } from '@shared/guards/referral-codes-enabled.guard';
import { NotForCoachGuard } from '@shared/guards/not-for-coach.guard';
import { OutgoingWebhooksEnabledGuard } from '@shared/guards/outgoing-webhooks-enabled.guard';

const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        loadChildren: () => import('./home/home.module').then(module => module.HomeModule),
        canActivate: [AuthorizationNeededGuard]
    },
    {
        path: 'mantis',
        loadChildren: () => import('./mantis-demo/mantis-demo.module').then(m => m.MantisDemoModule),
        canActivate: [AuthorizationNeededGuard]
    },
    {
        path: 'exercise',
        loadChildren: () => import('./exercise/exercise.module').then(module => module.ExerciseModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard],
    },
    {
        path: 'training',
        loadChildren: () => import('./training/training.module').then(module => module.TrainingModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'program',
        loadChildren: () => import('./program/program.module').then(m => m.ProgramModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'program-category',
        loadChildren: () => import('./program-category/program-category.module').then(module => module.ProgramCategoryModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'program-list',
        loadChildren: () => import('./program-list/program-list.module').then(module => module.ProgramListModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'program-tailor-made',
        loadChildren: () => import('./program-tailor-made/program-tailor-made.module').then(m => m.ProgramTailorMadeModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'muscle-group',
        loadChildren: () => import('./muscle-group/muscle-group.module').then(module => module.MuscleGroupModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'goal',
        loadChildren: () => import('./goal/goal.module').then(module => module.GoalModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'training-type',
        loadChildren: () => import('./training-type/training-type.module').then(module => module.TrainingTypeModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'workout-setup',
        loadChildren: () => import('./workout-setup/workout-setup.module').then(m => m.WorkoutSetupModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'environment',
        loadChildren: () => import('./environment/environment.module').then(module => module.EnvironmentModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'equipments',
        loadChildren: () => import('./equipment/equipment.module').then(module => module.EquipmentModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'instructor',
        loadChildren: () => import('./instructor/instructor.module').then(module => module.InstructorModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'training-list',
        loadChildren: () => import('./training-list/training-list.module').then(m => m.TrainingListModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'muscle-group-list',
        loadChildren: () => import('./muscle-group-list/muscle-group-list.module').then(m => m.MuscleGroupListModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'training-duration-list',
        loadChildren: () => import('./training-duration-list/training-duration-list.module').then(m => m.TrainingDurationListModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'training-duration-filter',
        loadChildren: () => import('./training-duration-filter/training-duration-filter.module').then(m => m.TrainingDurationFilterModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'training-group',
        loadChildren: () => import('./training-group/training-group.module').then(m => m.TrainingGroupModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'app-menu',
        loadChildren: () => import('./app-menu/app-menu.module').then(m => m.AppMenuModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'level',
        loadChildren: () => import('./level/level.module').then(module => module.LevelModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'meal',
        loadChildren: () => import('./meal/meal.module').then(m => m.MealModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'meal-group',
        loadChildren: () => import('./meal-group/meal-group.module').then(m => m.MealGroupModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'ingredient-group',
        loadChildren: () => import('./ingredient-group/ingredient-group.module').then(m => m.IngredientGroupModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'ingredient',
        loadChildren: () => import('./ingredient/ingredient.module').then(m => m.IngredientModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'nutrition-program',
        loadChildren: () => import('./nutrition-program/nutrition-program.module').then(m => m.NutritionProgramModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'nutrition-program-category',
        loadChildren: () => import('./nutrition-program-category/nutrition-program-category.module').then(module => module.NutritionProgramCategoryModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'nutrition-program-tailor-made',
        loadChildren: () => import('./nutrition-program-tailor-made/nutrition-program-tailor-made.module')
            .then(m => m.NutritionProgramTailorMadeModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard],
        data: { tailorMade: true }
    },
    {
        path: 'report-page',
        loadChildren: () => import('./report-page/report-page.module').then(module => module.ReportPageModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard]
    },
    {
        path: 'subscription',
        loadChildren: () => import('./subscription/subscription.module').then(module => module.SubscriptionModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'payment',
        loadChildren: () => import('./payment/payment.module').then(module => module.PaymentModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'outgoing-webhooks',
        loadChildren: () => import('./outgoing-webhooks/outgoing-webhooks.module').then(module => module.OutgoingWebhooksModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, OutgoingWebhooksEnabledGuard]
    },
    {
        path: 'charges',
        loadChildren: () => import('./charge/charge.module').then(module => module.ChargeModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'blog',
        loadChildren: () => import('./blog/blog.module').then(module => module.BlogModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'food-preference',
        loadChildren: () => import('./food-preference/food-preference.module').then(module => module.FoodPreferenceModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'diet',
        loadChildren: () => import('./diet/diet.module').then(module => module.DietModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'allergen',
        loadChildren: () => import('./allergen/allergen.module').then(module => module.AllergenModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'review',
        loadChildren: () => import('./review/review.module').then(module => module.ReviewModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'user',
        loadChildren: () => import('./user/user.module').then(module => module.UserModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'user-tag',
        loadChildren: () => import('./user-tag/user-tag.module').then(module => module.UserTagModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'user-readiness',
        loadChildren: () => import('./user-readiness/user-readiness.module').then(module => module.UserReadinessModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'user-completion',
        loadChildren: () => import('./user-completion/user-completion.module').then(module => module.UserCompletionModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'user-activity',
        loadChildren: () => import('./user-activity/user-activity.module').then(module => module.UserActivityModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'user-exercise-history',
        loadChildren: () => import('./user-exercise-history/user-exercise-history.module').then(module => module.UserExerciseHistoryModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'applications',
        loadChildren: () => import('./applications/applications.module').then(module => module.ApplicationsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'app-insight',
        loadChildren: () => import('./app-insight/app-insight.module').then(module => module.AppInsightModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, MainCmsGuard]
    },
    {
        path: 'live-events',
        loadChildren: () => import('./live-events/live-events.module').then(module => module.LiveEventsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    
    {
        path: 'feed',
        loadChildren: () => import('./feed/feed.module').then(module => module.FeedModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'translate',
        loadChildren: () => import('./translate/translate.module').then(module => module.TranslateModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'cms-translate',
        loadChildren: () => import('./cms-translate/cms-translate.module').then(module => module.CmsTranslateModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'mobile-config',
        loadChildren: () => import('./mobile-config/mobile-config.module').then(module => module.MobileConfigModule),
        canActivate: [AuthorizationNeededGuard, HasPinGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'mobile-config-legacy',
        pathMatch: 'full',
        redirectTo: 'mobile-config/legacy'
    },
    {
        path: 'achievements',
        loadChildren: () => import('./achievements/achievements.module').then(module => module.AchievementsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'coach-packages',
        loadChildren: () => import('./coach-packages/coach-packages.module').then(module => module.CoachPackagesModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'landing-page',
        loadChildren: () => import('./landing-page/landing-page.module').then(module => module.LandingPageModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'packages',
        loadChildren: () => import('./packages/packages.module').then(module => module.PackagesModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'package-tiers',
        loadChildren: () => import('./package-tiers/package-tiers.module').then(module => module.PackageTiersModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'exercise-library',
        loadChildren: () => import('./libraries/exercise/exercise.module').then(module => module.ExerciseLibraryModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'plan-requests',
        loadChildren: () => import('./program-requests/program-requests.module').then(module => module.ProgramRequestsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'recording',
        loadChildren: () => import('./recording/recording.module').then(module => module.RecordingModule),
        canActivate: [AuthorizationNeededGuard]
    },
    {
        path: 'resolution-center',
        loadChildren: () => import('./resolution-center-coach/resolution-center-coach.module')
            .then(module => module.ResolutionCenterCoachModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard]
    },
    {
        path: 'insight',
        loadChildren: () => import('./insights/insights.module').then(module => module.InsightsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'affiliate-partner',
        loadChildren: () => import('./affiliate-partners/affiliate-partners.module').then(module => module.AffiliatePartnersModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'referral-code',
        loadChildren: () => import('./referral-codes/referral-codes.module').then(module => module.ReferralCodesModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'referral-partner',
        loadChildren: () => import('./referral-partners/referral-partners.module').then(module => module.ReferralPartnersModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'referral-code-instructions',
        loadChildren: () => import('./referral-code-instructions/referral-code-instructions.module').then(module => module.ReferralCodeInstructionsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, ReferralCodesEnabledGuard]
    },
    {
        path: 'challenge',
        loadChildren: () => import('./challenge/challenge.module').then(module => module.ChallengeModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'lesson',
        loadChildren: () => import('./lesson/lesson.module').then(module => module.LessonModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, CoachingEnabledGuard]
    },
    {
        path: 'course',
        loadChildren: () => import('./course/course.module').then(module => module.CourseModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, CoachingEnabledGuard]
    },
    {
        path: 'lesson-type',
        loadChildren: () => import('./lesson-type/lesson-type.module').then(module => module.LessonTypeModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, CoachingEnabledGuard]
    },
    {
        path: 'lesson-goal',
        loadChildren: () => import('./lesson-goal/lesson-goal.module').then(module => module.LessonGoalModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, CoachingEnabledGuard]
    },
    {
        path: 'lesson-achievement',
        loadChildren: () => import('./lesson-achievement/lesson-achievement.module').then(module => module.LessonAchievementModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, CoachingEnabledGuard]
    },
    {
        path: 'course-category',
        loadChildren: () => import('./course-category/course-category.module').then(module => module.CourseCategoryModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, CoachingEnabledGuard]
    },
    {
        path: 'default-user-folder',
        loadChildren: () => import('./default-user-folder/default-user-folder.module').then(module => module.DefaultUserFolderModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, CoachingEnabledGuard]
    },
    {
        path: 'team',
        loadChildren: () => import('./team/team.module').then(module => module.TeamModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard, TeamsEnabledGuard]
    },
    {
        path: 'gift-card',
        loadChildren: () => import('./gift-card/gift-card.module').then(module => module.GiftCardModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, GiftCardsEnabledGuard]
    },
    {
        path: 'app-tryout',
        loadChildren: () => import('./app-tryout/app-tryout.module').then(module => module.AppTryoutModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, AppTryoutEnabledGuard]
    },
    {
        path: 'profile',
        loadChildren: () => import('./profile/profile.module').then(module => module.ProfileModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, ProfileRootGuard]
    },
    {
        path: 'preview',
        loadChildren: () => import('./shared/components/import-library/import-library.module').then(module => module.ImportLibraryModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard]
    },
    {
        path: 'statistics',
        loadChildren: () => import('./app-analytics/app-analytics.module')
            .then(module => module.AppAnalyticsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'reports',
        loadChildren: () => import('./app-analytics/app-analytics.module')
            .then(module => module.AppAnalyticsModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'business-intelligence',
        loadChildren: () => import('./business-intelligence/business-intelligence.module')
            .then(module => module.BusinessIntelligenceModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'help',
        loadChildren: () => import('./help/help.module').then(module => module.HelpModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard]
    },
    {
        path: 'user-search',
        loadChildren: () => import('./user-search/user-search.module').then(module => module.UserSearchModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'scheduler',
        loadChildren: () => import('./scheduler/scheduler.module').then(module => module.SchedulerModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard, SchedulerEnabledGuard]
    },
    {
        path: 'questionnaire',
        loadChildren: () => import('./questionnaire/questionnaire.module').then(module => module.QuestionnaireModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'finished-questionnaires',
        loadChildren: () => import('./questionnaire-finished/questionnaire-finished.module').then(module => module.QuestionnaireFinishedModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },
    {
        path: 'client-messages',
        pathMatch: 'full',
        loadChildren: () => import('./client-messages/client-messages.module').then(module => module.ClientMessagesModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard, MessagingEnabledGuard]
    },
    {
        path: 'client-messages/:id',
        loadChildren: () => import('./client-messages/client-messages.module').then(module => module.ClientMessagesModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard, MessagingEnabledGuard]
    },
    {
        path: 'client-messages-legacy',
        pathMatch: 'full',
        loadChildren: () => import('./client-messages/client-messages.module').then(module => module.ClientMessagesModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, MessagingEnabledGuard]
    },
    {
        path: 'client-messages-legacy/:id',
        loadChildren: () => import('./client-messages/client-messages.module').then(module => module.ClientMessagesModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, MessagingEnabledGuard]
    },
    {
        path: 'client-messages-mantis',
        pathMatch: 'full',
        redirectTo: 'client-messages'
    },
    {
        path: 'client-messages-mantis/:id',
        redirectTo: 'client-messages/:id'
    },
    {
        path: 'chat-rooms',
        loadChildren: () => import('./chat-rooms/chat-rooms.module').then(module => module.ChatRoomsModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard, GroupChatEnabledGuard]
    },
    {
        path: 'broadcast-messages',
        loadChildren: () => import('./broadcast-messages/broadcast-messages.module').then(module => module.BroadcastMessagesModule),
        canActivate: [AuthorizationNeededGuard, isSuspendedGuard, MessagingEnabledGuard]
    },
    {
        path: 'reported-messages',
        loadChildren: () => import('./reported-messages/reported-messages.module').then(module => module.ReportedMessagesModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard, GroupChatEnabledGuard]
    },
    {
        path: 'promo-codes',
        loadChildren: () => import('./promo-codes/promo-codes.module').then(module => module.PromoCodesModule),
        canActivate: [AuthorizationNeededGuard, NotForCoachGuard, isSuspendedGuard]
    },

    {
        path: 'auth',
        loadChildren: () => import('./auth/auth.module').then(module => module.AuthModule)
    },
    { path: '**', redirectTo: '/', pathMatch: 'full' }
];
@NgModule({
    imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
    exports: [RouterModule]
})
export class AppRoutingModule { }
